import { useAuth, useUser } from "@clerk/clerk-react";
import type { UserResource } from "@clerk/types";
import { normalizeRoleFromUnknown, type UserRole } from "@/lib/auth";

function metaAllowsGeoSim(meta: Record<string, unknown> | null | undefined): boolean {
  if (!meta || typeof meta !== "object") return false;
  if (meta.qa === true || meta.quality_assurance === true) return true;
  if (meta.super_admin === true || meta.is_super_admin === true) return true;
  const role = meta.role;
  if (
    role === "super_admin" ||
    role === "superadmin" ||
    role === "super-admin" ||
    role === "qa" ||
    role === "quality_assurance"
  ) {
    return true;
  }
  return false;
}

export function userCanUseGeoSim(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  const pub = (user.publicMetadata ?? {}) as Record<string, unknown>;
  const unsafe = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
  if (normalizeRoleFromUnknown(pub.role) === "super_admin") return true;
  if (metaAllowsGeoSim(pub)) return true;
  // Some orgs put flags only in unsafeMetadata (not ideal, but support for QA)
  if (metaAllowsGeoSim(unsafe)) return true;
  return false;
}

/**
 * super_admin (role or boolean flags) or QA.
 * Uses useAuth().isLoaded so we don't hide forever if useUser is slow.
 */
export function useCanUseGeoSim(): { canUse: boolean; ready: boolean; signedIn: boolean } {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();

  const ready = authLoaded && (!isSignedIn || userLoaded);
  if (!ready) return { canUse: false, ready: false, signedIn: !!isSignedIn };
  if (!isSignedIn || !user) return { canUse: false, ready: true, signedIn: false };

  const role: UserRole = normalizeRoleFromUnknown(user.publicMetadata?.role);
  if (role === "super_admin") return { canUse: true, ready: true, signedIn: true };
  if (userCanUseGeoSim(user)) return { canUse: true, ready: true, signedIn: true };
  return { canUse: false, ready: true, signedIn: true };
}
